import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import dynamic from 'next/dynamic';

import omit from 'lodash/omit';

import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import useStories from '@/utils/hooks/swr/useStories';
import useTranslation from '@/utils/hooks/translation/useTranslation';
// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import useLabelsAlbumsOnMedia from '@/utils/hooks/utils/useLabelsAlbumsOnMedia';

import PropTypes from 'prop-types';

import { TAB_STORIES_INSTAGRAM } from '@/lib/myprofile/tabs';
import { CUSTOM } from '@/lib/periods';
//lib
import { storiesSortOptions } from '@/lib/posts/postSortOptions';
import { EXPORT_ENDPOINT_TYPES, STORY } from '@/lib/posts/postTypes';
import { CONTENT_POSTS } from '@/lib/routes/privateRoutes';
import { INSTAGRAM } from '@/lib/socialProfile';

import Carousel from '@/components/base/Carousel';
import Loader from '@/components/base/Loader';
import { PostComponent } from '@/components/base/Post/PostComponent';
//components
import { WidgetMedia } from '@/components/base/WidgetMedia';
import ButtonDownloadDirectExport from '@/components/composite/Button/ButtonDownloadDirectExport';
import CustomDashboardWidgetActions from '@/components/pages/custom-dashboards/dashboard/CustomDashboardWidgetActions';
const CustomDashboardWidgetOptionSaver = dynamic(
    () => import('@/components/pages/custom-dashboards/dashboard/CustomDashboardWidgetOptionSaver')
);

//composites
const MediaViewerModal = dynamic(
    () => import('@/components/composite/MediaViewer/MediaViewerModal')
);

export const StoriesWidget = ({
    sortFields,
    periodData,
    numberFormat,
    isDashboard,
    onDelete,
    config
}) => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { t } = useTranslation('commons');
    const sortOptions = storiesSortOptions(t)
        ?.[INSTAGRAM]?.filter((element) => sortFields?.includes(element?.value))
        ?.sort((a, b) => sortFields?.indexOf(a?.value) - sortFields?.indexOf(b?.value));
    const [socialProfileSelected, setSocialProfileSelected] = useState(
        isDashboard
            ? activatedSocialProfiles?.find((sp) => sp?.id === config.social_profiles[0])
            : activeSocialProfile
    );
    const [isLoadingExportXlsDashboard, setIsLoadingExportXlsDashboard] = useState(false);
    const [mediaViewerParams, setMediaViewerParams] = useState({ isOpen: false, mediaId: null });

    const {
        dataStories,
        requestParams,
        storiesError,
        // Loaders
        isLoadingInitialData,
        // Functions
        mutateStories,
        setRequestParams
    } = useStories({
        activeSPID: socialProfileSelected?.id,
        limit: 10
    });

    useEffect(() => {
        if (periodData.period === CUSTOM) {
            setRequestParams((prevState) => ({
                ...prevState,
                period: periodData.period,
                start: periodData.start,
                end: periodData.end,
                comparison_period: periodData.compareTo,
                with_labels: true,
                with_albums: true
            }));
        } else {
            setRequestParams((prevState) => ({
                ...omit(prevState, ['start', 'end']),
                period: periodData.period,
                comparison_period: periodData.compareTo,
                with_labels: true,
                with_albums: true
            }));
        }
    }, [
        periodData.period,
        periodData.start,
        periodData.end,
        periodData.compareTo,
        setRequestParams
    ]);

    // relaunch request if active SocialProfile changes
    useEffect(() => {
        if (!isDashboard) {
            setSocialProfileSelected(activeSocialProfile);
        }
        if (
            sortOptions.filter((el) => requestParams?.filter_post_type?.includes(el.value))
                ?.length === 0
        ) {
            setRequestParams((prevState) => ({
                ...prevState,
                filter_post_type: []
            }));
        }
    }, [activeSocialProfile?.id]);

    const { handleAlbumsEvent, handleLabelsEvent } = useLabelsAlbumsOnMedia({
        mutateMedias: mutateStories,
        requestParams,
        type: 'stories'
    });

    const canExportStories =
        !isLoadingInitialData && dataStories && dataStories[0]?.stories?.length > 0;

    const onClickDashboardXlsExport = async (callback) => {
        setIsLoadingExportXlsDashboard(true);
        await callback();
        setIsLoadingExportXlsDashboard(false);
    };

    return (
        <WidgetMedia>
            <WidgetMedia.Header>
                <div className="flex items-center space-x-3">
                    {isDashboard ? (
                        <>
                            <div>
                                <CustomDashboardWidgetActions.SelectSP
                                    config={config}
                                    socialProfiles={config?.social_profiles}
                                    setSocialProfileSelected={setSocialProfileSelected}
                                    isMedia
                                />
                            </div>
                            <CustomDashboardWidgetOptionSaver>
                                {(saveWidgetOption) => (
                                    <div className="w-52">
                                        <WidgetMedia.SortBy
                                            network={INSTAGRAM}
                                            setRequestParams={setRequestParams}
                                            data={sortOptions}
                                            currentSort={
                                                config?.parameters
                                                    ? config?.parameters?.sort
                                                    : requestParams?.sort
                                            }
                                            onChange={(sort) => {
                                                saveWidgetOption(
                                                    config,
                                                    'sort',
                                                    {
                                                        order: sort.order,
                                                        column: sort.value
                                                    },
                                                    {
                                                        order: sortOptions[0].order,
                                                        column: sortOptions[0].value
                                                    }
                                                );
                                            }}
                                        />
                                    </div>
                                )}
                            </CustomDashboardWidgetOptionSaver>
                        </>
                    ) : (
                        <div className="w-52">
                            <WidgetMedia.SortBy
                                network={INSTAGRAM}
                                setRequestParams={setRequestParams}
                                data={sortOptions}
                            />
                        </div>
                    )}
                </div>
                {isDashboard ? (
                    <CustomDashboardWidgetActions.Popover className="bg-white dark:bg-icoDarkMode-deepBlack">
                        {canExportStories ? (
                            <li className="rounded font-GMedium text-xs text-icoGray-500 transition-all hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoDarkMode-deepBlack">
                                <ButtonDownloadDirectExport
                                    sortOptions={sortOptions}
                                    requestParams={requestParams}
                                    isLoading={isLoadingExportXlsDashboard}
                                    typeExport={EXPORT_ENDPOINT_TYPES[STORY]}
                                    currentSocialProfile={socialProfileSelected}>
                                    {({ generateReport, isLoading }) => (
                                        <button
                                            disabled={isLoading}
                                            className="flex w-full items-center p-2 disabled:text-icoGray-500 disabled:dark:text-icoDarkMode-wolf"
                                            onClick={() =>
                                                onClickDashboardXlsExport(generateReport)
                                            }>
                                            {isLoading ? <Loader className="mr-1 h-3 w-3" /> : null}
                                            {t('analytics:xls_export')}
                                        </button>
                                    )}
                                </ButtonDownloadDirectExport>
                            </li>
                        ) : null}
                        <li className="rounded font-GMedium text-xs text-icoGray-500 transition-all hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoDarkMode-deepBlack">
                            <button className="w-full p-2 text-left" onClick={onDelete}>
                                {t('dashboard:remove_widget')}
                            </button>
                        </li>
                    </CustomDashboardWidgetActions.Popover>
                ) : canExportStories ? (
                    <ButtonDownloadDirectExport
                        sortOptions={sortOptions}
                        requestParams={requestParams}
                        typeExport={EXPORT_ENDPOINT_TYPES[STORY]}
                        currentSocialProfile={socialProfileSelected}>
                        {({ generateReport, isLoading }) => (
                            <WidgetMedia.ExportButton
                                onClick={generateReport}
                                isLoading={isLoading}
                                tooltipContent={t('analytics:xls_export')}
                            />
                        )}
                    </ButtonDownloadDirectExport>
                ) : null}
            </WidgetMedia.Header>

            {isLoadingInitialData && !dataStories ? (
                <WidgetMedia.Placeholder
                    placeholderConfig={{ network: INSTAGRAM, type: 'stories' }}
                    wrapperClass="lg:space-x-m 2xl:space-x-5"
                    postClass="min-w-[calc(25%-6px)] lg:min-w-[calc(20%-14.5px)] 2xl:min-w-[calc(14.28%-17px)]"
                    postFormat="story"
                />
            ) : null}
            {!isLoadingInitialData && dataStories && dataStories[0]?.stories?.length === 0 ? (
                <WidgetMedia.NoData networks={[INSTAGRAM]} message={t('no_stories_in_period')} />
            ) : null}

            {storiesError && !isLoadingInitialData ? (
                <WidgetMedia.ErrorFallBack resetErrorBoundary={() => mutateStories()} />
            ) : null}

            {!isLoadingInitialData && dataStories && dataStories[0]?.stories?.length > 0 ? (
                <ErrorBoundary
                    FallbackComponent={WidgetMedia.ErrorFallBack}
                    onReset={() => mutateStories()}>
                    <WidgetMedia.Carousel
                        navPosition="external"
                        className="lg:space-x-m 2xl:space-x-5">
                        {dataStories?.map((stories) =>
                            stories?.['stories']?.map((media) => (
                                <Carousel.Slide
                                    key={media.id}
                                    className="min-w-[calc(25%-6px)] lg:min-w-[calc(20%-14.5px)] 2xl:min-w-[calc(14.28%-17px)]">
                                    <PostComponent
                                        network={INSTAGRAM}
                                        type="stories"
                                        isLoading={false}
                                        data={media}
                                        mutateMedias={mutateStories}
                                        config={{
                                            postFormat: 'story',
                                            postType: 'story',
                                            activeFilter: requestParams?.sort?.column ?? null,
                                            network: INSTAGRAM,
                                            numberFormat: numberFormat
                                        }}
                                        handleLabelsEvent={handleLabelsEvent}
                                        handleAlbumsEvent={handleAlbumsEvent}
                                        openViewer={(e) => {
                                            setMediaViewerParams({
                                                isOpen: true,
                                                mediaId: media?.id
                                            });
                                            e.stopPropagation();
                                        }}
                                    />
                                </Carousel.Slide>
                            ))
                        )}
                        <Carousel.Slide
                            arial-label="show-more"
                            className="group min-w-[calc(25%-6px)] lg:min-w-[calc(20%-14.5px)] 2xl:min-w-[calc(14.28%-17px)]">
                            <WidgetMedia.ShowMore
                                url={`${CONTENT_POSTS}/?tab=${TAB_STORIES_INSTAGRAM}&column=${
                                    requestParams?.sort?.column
                                }&order=${requestParams?.sort?.order}&analytics=true${
                                    isDashboard
                                        ? `&socialProfileId=${socialProfileSelected.id}`
                                        : ''
                                }`}
                                textContent={t('show_more_stories')}
                            />
                        </Carousel.Slide>
                    </WidgetMedia.Carousel>
                </ErrorBoundary>
            ) : null}
            <MediaViewerModal
                mutateMedias={mutateStories}
                isOpen={mediaViewerParams?.isOpen}
                toggle={() => {
                    setMediaViewerParams({ ...mediaViewerParams, isOpen: false });
                }}
                socialProfile={socialProfileSelected}
                mediaId={mediaViewerParams?.mediaId}
                mediaType={STORY}
                mediaRequestParams={requestParams}
            />
        </WidgetMedia>
    );
};

StoriesWidget.propTypes = {
    sortFields: PropTypes.arrayOf(PropTypes.string),
    periodData: PropTypes.object,
    numberFormat: PropTypes.string
};
